import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserAdminService } from '@nebalkon-workspace/admin-api';
import {
  CABINET_PATHS,
  CABINET_QR_SCAN,
} from '@nebalkon-workspace/cabinet-core';
import {
  BoxService,
  ItemService,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import { NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { firstValueFrom } from 'rxjs';
import { staffCabinetNavLink } from '../navigation/staff-cabinet-nav.util';
import { parseQrToBoxOrItemId } from '../utils/qr-storage-entity.util';
import { warehouseRouteSegment } from '../utils/warehouse-route.util';

export type StaffQrScanParsedEntity =
  | { box_id: string }
  | { item_id: string }
  | { user_id: string }
  | { cell_id: string };

export interface StaffQrScanNavigateOptions {
  relativeTo?: ActivatedRoute;
}

export interface StaffQrScanAndNavigateOptions extends StaffQrScanNavigateOptions {
  /** Navigate to manual scan page when the camera is unavailable. */
  fallbackToManualScan?: boolean;
}

@Injectable({ providedIn: 'root' })
export class StaffQrScanFlowService {
  readonly #qrScan = inject(CABINET_QR_SCAN);
  readonly #router = inject(Router);
  readonly #paths = inject(CABINET_PATHS);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #boxService = inject(BoxService);
  readonly #itemService = inject(ItemService);
  readonly #userAdmin = inject(UserAdminService);
  readonly #warehouseService = inject(WarehouseService);

  parseRawCode(raw: string): StaffQrScanParsedEntity | null {
    return parseQrToBoxOrItemId(raw);
  }

  showInvalidQrToast(): void {
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [this.#translate.instant('admin.staffQrScan.invalidQr')],
    });
  }

  buildScanQueryParams(
    parsed: StaffQrScanParsedEntity,
  ): { box: string | null; item: string | null } {
    if ('box_id' in parsed) {
      return { box: parsed.box_id, item: null };
    }

    if ('item_id' in parsed) {
      return { box: null, item: parsed.item_id };
    }

    return { box: null, item: null };
  }

  async navigateToScanResult(
    parsed: StaffQrScanParsedEntity,
    options?: StaffQrScanNavigateOptions,
  ): Promise<boolean> {
    if ('user_id' in parsed) {
      await this.#router.navigate([
        this.#paths.staff,
        'users',
        parsed.user_id,
      ]);
      return true;
    }

    const queryParams = this.buildScanQueryParams(parsed);

    if (options?.relativeTo) {
      await this.#router.navigate([], {
        relativeTo: options.relativeTo,
        queryParams,
      });
      return true;
    }

    await this.#router.navigate([staffCabinetNavLink('scan', this.#paths)], {
      queryParams,
    });
    return true;
  }

  async resolveAndNavigate(
    raw: string,
    options?: StaffQrScanNavigateOptions,
  ): Promise<StaffQrScanParsedEntity | null> {
    const parsed = this.parseRawCode(raw);
    if (!parsed) {
      this.showInvalidQrToast();
      return null;
    }

    if ('cell_id' in parsed) {
      const resolved = await this.#navigateToCellPage(parsed.cell_id);
      if (!resolved) {
        this.showInvalidQrToast();
      }
      return resolved;
    }

    if ('user_id' in parsed || 'item_id' in parsed) {
      await this.navigateToScanResult(parsed, options);
      return parsed;
    }

    if ('box_id' in parsed) {
      return this.#resolveEntityId(parsed.box_id, options);
    }

    return null;
  }

  navigateFromRawCode(
    raw: string,
    options?: StaffQrScanNavigateOptions,
  ): StaffQrScanParsedEntity | null {
    const parsed = this.parseRawCode(raw);
    if (!parsed) {
      this.showInvalidQrToast();
      return null;
    }

    if ('cell_id' in parsed) {
      void this.#navigateToCellPage(parsed.cell_id).then((resolved) => {
        if (!resolved) {
          this.showInvalidQrToast();
        }
      });
      return parsed;
    }

    void this.navigateToScanResult(parsed, options);
    return parsed;
  }

  async scanAndNavigateToResult(
    options?: StaffQrScanAndNavigateOptions,
  ): Promise<StaffQrScanParsedEntity | null> {
    const outcome = await this.#qrScan.scanQrCode();

    if (outcome.kind === 'success') {
      return this.resolveAndNavigate(outcome.value, options);
    }

    if (outcome.kind === 'unsupportedEnvironment') {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'info',
        text: [this.#translate.instant('qrScanner.nativeOnly')],
      });

      if (options?.fallbackToManualScan) {
        void this.#router.navigateByUrl(
          staffCabinetNavLink('scan', this.#paths),
        );
      }
    }

    return null;
  }

  async #resolveEntityId(
    id: string,
    options?: StaffQrScanNavigateOptions,
  ): Promise<StaffQrScanParsedEntity | null> {
    try {
      await firstValueFrom(this.#boxService.getAdminBox(id));
      await this.navigateToScanResult({ box_id: id }, options);
      return { box_id: id };
    } catch {
      /* try item */
    }

    try {
      await firstValueFrom(this.#itemService.getAdminItem(id));
      await this.navigateToScanResult({ item_id: id }, options);
      return { item_id: id };
    } catch {
      /* try user */
    }

    try {
      await firstValueFrom(this.#userAdmin.getUser(id));
      await this.#router.navigate([this.#paths.staff, 'users', id]);
      return { user_id: id };
    } catch {
      /* try cell */
    }

    const cellResolved = await this.#navigateToCellPage(id);
    if (cellResolved) {
      return cellResolved;
    }

    this.showInvalidQrToast();
    return null;
  }

  async #navigateToCellPage(
    cellId: string,
  ): Promise<StaffQrScanParsedEntity | null> {
    try {
      const contents = await firstValueFrom(
        this.#warehouseService.getWarehouseCellContents(cellId),
      );
      const segment = warehouseRouteSegment({
        id: contents.warehouse.id,
        slug: contents.warehouse.slug,
      });
      await this.#router.navigate([
        this.#paths.staff,
        'warehouses',
        segment,
        'cells',
        cellId,
      ]);
      return { cell_id: cellId };
    } catch {
      return null;
    }
  }
}
